import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

interface Testimonial {
  name: string;
  role: string;
  roleEn: string;
  content: string;
  contentEn: string;
  rating: number;
  image?: string;
}

const testimonials: Testimonial[] = [
  {
    name: "Μαρία Παπαδοπούλου",
    role: "Διαχειρίστρια Πολυκατοικίας",
    roleEn: "Building Manager",
    content: "Η LIFTCO μας βοήθησε να εκσυγχρονίσουμε τον ανελκυστήρα μας με άψογο επαγγελματισμό. Η συνέπεια και η ποιότητα της δουλειάς τους είναι εξαιρετική!",
    contentEn: "LIFTCO helped us modernize our elevator with impeccable professionalism. Their consistency and quality of work is exceptional!",
    rating: 5,
  },
  {
    name: "Γιώργος Αντωνίου",
    role: "Ιδιοκτήτης Ξενοδοχείου",
    roleEn: "Hotel Owner",
    content: "Συνεργαζόμαστε με τη LIFTCO εδώ και 15 χρόνια. Η 24ωρη υποστήριξή τους μας έχει σώσει πολλές φορές. Απόλυτα αξιόπιστοι!",
    contentEn: "We have been working with LIFTCO for 15 years. Their 24/7 support has saved us many times. Absolutely reliable!",
    rating: 5,
  },
  {
    name: "Ελένη Καραγιάννη",
    role: "Διευθύντρια Εμπορικού Κέντρου",
    roleEn: "Shopping Center Director",
    content: "Η εγκατάσταση των ανελκυστήρων φορτίων έγινε εντός χρονοδιαγράμματος και budget. Εξαιρετική ομάδα με βαθιά γνώση του αντικειμένου.",
    contentEn: "The freight elevator installation was completed on time and within budget. Excellent team with deep expertise.",
    rating: 5,
  },
  {
    name: "Νίκος Δημητρίου",
    role: "Αρχιτέκτονας",
    roleEn: "Architect",
    content: "Προτείνω τη LIFTCO σε όλους τους πελάτες μου. Ο σχεδιασμός και η αισθητική των ανελκυστήρων τους ταιριάζει άψογα με κάθε αρχιτεκτονικό ύφος.",
    contentEn: "I recommend LIFTCO to all my clients. The design and aesthetics of their elevators fit perfectly with any architectural style.",
    rating: 5,
  },
  {
    name: "Σοφία Νικολάου",
    role: "Ιδιοκτήτρια Κλινικής",
    roleEn: "Clinic Owner",
    content: "Η εγκατάσταση του ανελκυστήρα ΑΜΕΑ ήταν άριστη. Οι τεχνικοί ήταν ευγενικοί και επαγγελματίες. Συστήνω ανεπιφύλακτα!",
    contentEn: "The accessibility elevator installation was excellent. The technicians were polite and professional. Highly recommended!",
    rating: 5,
  },
];

const TestimonialsCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const next = () => {
    setIsAutoPlaying(false);
    setCurrent((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setIsAutoPlaying(false);
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goTo = (index: number) => {
    setIsAutoPlaying(false);
    setCurrent(index);
  };

  const currentTestimonial = testimonials[current];
  const content = language === "en" ? currentTestimonial.contentEn : currentTestimonial.content;
  const role = language === "en" ? currentTestimonial.roleEn : currentTestimonial.role;

  return (
    <section className="py-20 bg-primary relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-white" />
        <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full bg-white" />
      </div>

      <div className="container relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            {t("testimonials.title")}
          </h2>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto">
            {t("testimonials.subtitle")}
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl"
            >
              {/* Quote icon */}
              <Quote className="w-12 h-12 text-primary/20 mb-6" />

              {/* Content */}
              <p className="text-lg md:text-xl text-foreground leading-relaxed mb-8">
                "{content}"
              </p>

              {/* Rating */}
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < currentTestimonial.rating
                        ? "text-yellow-500 fill-yellow-500"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary">
                    {currentTestimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-base">
                    {currentTestimonial.name}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {role}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation arrows */}
          <Button
            variant="outline"
            size="icon"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white hover:bg-gray-100 border-none shadow-lg"
            onClick={prev}
            aria-label="Προηγούμενη μαρτυρία"
          >
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white hover:bg-gray-100 border-none shadow-lg"
            onClick={next}
            aria-label="Επόμενη μαρτυρία"
          >
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Μαρτυρίες πελατών">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              aria-label={`Μαρτυρία ${index + 1}`}
              aria-selected={index === current}
              role="tab"
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === current
                  ? "bg-white w-8"
                  : "bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
